import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Mail, Phone, User, ShieldCheck, CreditCard, Plus, Trash2, Clock, Info } from "lucide-react";
import { toast } from "sonner";

function Row({ icon: Icon, label, value, extra }) {
  return (
    <div className="cp-row">
      <Icon className="cp-row__icon" size={16} />
      <div style={{ flex: 1 }}>
        <div className="cp-row__label">{label}</div>
        <div className="cp-row__value">{value}{extra}</div>
      </div>
    </div>
  );
}

export default function ClientProfile() {
  const [me, setMe] = useState(null);
  const [pmData, setPmData] = useState({ ttl_days: 30, methods: [] });
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ kind: "card", brand: "", last4: "", holder: "", exp_month: "", exp_year: "" });

  const loadMe = () => api.get("/client/me").then((r) => setMe(r.data)).catch(() => {});
  const loadPm = () => api.get("/client/payment-methods").then((r) => setPmData(r.data)).catch(() => {});
  useEffect(() => { loadMe(); loadPm(); }, []);

  if (!me) return <div className="cp-empty" data-testid="client-profile-loading">A carregar…</div>;

  const submitPm = async (e) => {
    e.preventDefault();
    try {
      await api.post("/client/payment-methods", {
        ...form,
        exp_month: form.exp_month ? Number(form.exp_month) : null,
        exp_year: form.exp_year ? Number(form.exp_year) : null,
      });
      toast.success("Método adicionado (auto-apaga em 30 dias)");
      setForm({ kind: "card", brand: "", last4: "", holder: "", exp_month: "", exp_year: "" });
      setShowAdd(false);
      loadPm();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removePm = async (id) => {
    if (!window.confirm("Remover este método de pagamento?")) return;
    try {
      await api.delete(`/client/payment-methods/${id}`);
      toast.success("Removido");
      loadPm();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const verifiedBadge = me.email_verified ? (
    <span className="cp-pill cp-pill--ok" style={{ marginLeft: 10, padding: "3px 8px", fontSize: "0.62rem" }}>
      <ShieldCheck size={10} /> Verificado
    </span>
  ) : null;

  return (
    <div data-testid="client-profile-page">
      <div className="cp-page-head">
        <span className="cp-eyebrow">Perfil</span>
        <h1 className="cp-title">A sua conta</h1>
        <p className="cp-desc">Informação pessoal e métodos de pagamento — sempre sob o seu controlo.</p>
      </div>

      <div className="cp-card" style={{ padding: "8px 24px", marginBottom: 28 }}>
        <Row icon={User} label="Nome" value={me.name} />
        <Row icon={Mail} label="Email" value={me.email} extra={verifiedBadge} />
        <Row icon={Phone} label="Telefone" value={me.phone || "—"} />
        <Row icon={User} label="Membro desde" value={new Date(me.created_at).toLocaleDateString()} />
      </div>

      {/* Payment methods */}
      <div className="cp-card" data-testid="client-payment-methods-block">
        <div className="cp-section-head">
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <CreditCard size={16} style={{ color: "#f59e0b" }} />
              <div className="cp-section-head__title">Métodos de pagamento</div>
            </div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginTop: 8, color: "#94a3b8", fontSize: "0.8rem", lineHeight: 1.5 }}>
              <Info size={13} style={{ marginTop: 3, flexShrink: 0 }} />
              <span>
                Guardados até <strong style={{ color: "#f5f0e6" }}>{pmData.ttl_days} dias</strong>. Após esse período são{" "}
                <strong style={{ color: "#f5f0e6" }}>totalmente apagados</strong> por segurança.
              </span>
            </div>
          </div>
          {!showAdd && (
            <button onClick={() => setShowAdd(true)} data-testid="client-pm-add-btn" className="cp-btn-outline">
              <Plus size={12} /> Adicionar
            </button>
          )}
        </div>

        {showAdd && (
          <form onSubmit={submitPm} style={{ background: "rgba(245,158,11,0.04)", border: "1px solid rgba(245,158,11,0.25)", borderRadius: 4, padding: 20, marginBottom: 20 }} data-testid="client-pm-form">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <label style={{ gridColumn: "span 2" }}>
                <span className="cp-label">Tipo</span>
                <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="cp-select" data-testid="pm-kind">
                  <option value="card">Cartão</option>
                  <option value="mbway">MB WAY</option>
                  <option value="pix">Pix</option>
                  <option value="apple_pay">Apple Pay</option>
                  <option value="google_pay">Google Pay</option>
                </select>
              </label>
              {form.kind === "card" && (
                <>
                  <label>
                    <span className="cp-label">Marca</span>
                    <input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Visa" className="cp-input" data-testid="pm-brand" />
                  </label>
                  <label>
                    <span className="cp-label">Últimos 4 dígitos</span>
                    <input maxLength={4} value={form.last4} onChange={(e) => setForm({ ...form, last4: e.target.value.replace(/\D/g, "") })} placeholder="4242" className="cp-input" data-testid="pm-last4" />
                  </label>
                  <label style={{ gridColumn: "span 2" }}>
                    <span className="cp-label">Titular</span>
                    <input value={form.holder} onChange={(e) => setForm({ ...form, holder: e.target.value })} placeholder="Nome no cartão" className="cp-input" />
                  </label>
                  <label>
                    <span className="cp-label">Mês exp.</span>
                    <input type="number" min="1" max="12" value={form.exp_month} onChange={(e) => setForm({ ...form, exp_month: e.target.value })} placeholder="MM" className="cp-input" />
                  </label>
                  <label>
                    <span className="cp-label">Ano exp.</span>
                    <input type="number" min="2026" max="2040" value={form.exp_year} onChange={(e) => setForm({ ...form, exp_year: e.target.value })} placeholder="YYYY" className="cp-input" />
                  </label>
                </>
              )}
              {form.kind === "mbway" && (
                <label style={{ gridColumn: "span 2" }}>
                  <span className="cp-label">Telemóvel MB WAY</span>
                  <input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+351 …" className="cp-input" />
                </label>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 18 }}>
              <button type="button" onClick={() => setShowAdd(false)} className="cp-btn-ghost" style={{ justifyContent: "center", height: 42 }} data-testid="pm-cancel">
                Cancelar
              </button>
              <button type="submit" className="cp-btn-primary" style={{ justifyContent: "center", height: 42 }} data-testid="pm-submit">
                Guardar (30 dias)
              </button>
            </div>
            <div style={{ marginTop: 12, fontSize: "0.72rem", color: "#94a3b8", lineHeight: 1.55 }}>
              Nunca guardamos o CVV nem o PAN completo. Apenas os últimos 4 dígitos, marca e um token opaco do gateway.
            </div>
          </form>
        )}

        {pmData.methods.length === 0 && !showAdd ? (
          <div className="cp-empty" style={{ padding: "40px 20px" }}>
            Ainda não guardou nenhum método de pagamento.
          </div>
        ) : (
          <div>
            {pmData.methods.map((m) => (
              <div key={m.id} className="cp-pm-row" data-testid={`client-pm-${m.id}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div className="cp-pm-badge">{m.brand || m.kind}</div>
                  <div>
                    <div style={{ fontSize: "0.9rem", color: "#f5f0e6", fontWeight: 600 }}>
                      {m.kind === "card" ? `**** ${m.last4}` : m.kind === "mbway" ? (m.phone || "MB WAY") : m.label}
                    </div>
                    <div style={{ fontSize: "0.72rem", color: "#94a3b8", display: "flex", alignItems: "center", gap: 10, marginTop: 2 }}>
                      {m.holder && <span>{m.holder}</span>}
                      {m.exp_month && m.exp_year && <span>· {String(m.exp_month).padStart(2, "0")}/{String(m.exp_year).slice(-2)}</span>}
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#f59e0b" }}>
                        <Clock size={10} /> {m.days_left ?? "?"}d restantes
                      </span>
                    </div>
                  </div>
                </div>
                <button onClick={() => removePm(m.id)} data-testid={`pm-delete-${m.id}`} className="cp-icon-btn" aria-label="Remover">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <p style={{ marginTop: 16, fontSize: "0.8rem", color: "#94a3b8" }}>
        Precisa alterar algo? Contacte{" "}
        <a href="mailto:apoio@ditours.pt" style={{ color: "#f59e0b", fontWeight: 700 }}>apoio@ditours.pt</a>.
      </p>
    </div>
  );
}
